import React, { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import api from "@/lib/api";
import { formatDOP } from "@/lib/format";
import {
  DEFAULT_SEARCH_FILTERS, buildProductsQuery,
} from "@/components/ProductSearchControls";

/**
 * Buscador de productos server-side reutilizable.
 *
 * Comportamiento (mismo patrón que POS):
 *  - Input con debounce (250ms) → GET /products?q=…&in_sku/name/barcode/description
 *  - Enter inteligente:
 *      1) Si en los resultados actuales hay un SKU con match EXACTO → selecciona.
 *      2) Si no y hay una sola coincidencia visible → selecciona.
 *      3) Si no, consulta /products/by-sku/{q} en toda la DB.
 *      4) Si no hay match único, muestra mensaje sin resultados.
 *  - Click en un item de la lista → selecciona.
 *
 * Props:
 *  - onSelect(product): callback al seleccionar.
 *  - showCost: bool. Si true, muestra `cost` en la fila (para Compras). Default: false (muestra price).
 *  - onlyActive: bool. Default true.
 *  - placeholder: string.
 *  - testidPrefix: string. Namespace para data-testid.
 *  - pageSize: número de resultados por búsqueda. Default 30.
 */
export default function ProductPicker({
  onSelect,
  showCost = false,
  onlyActive = true,
  placeholder = "Buscar por SKU, nombre, código de barras…",
  testidPrefix = "picker",
  pageSize = 30,
}) {
  const [search, setSearch] = useState("");
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);
  const [notice, setNotice] = useState("");

  const filters = DEFAULT_SEARCH_FILTERS; // sku + name + barcode + description(off)

  const loadProducts = async () => {
    setLoading(true);
    try {
      const qs = buildProductsQuery({
        q: search, filters, page: 1, pageSize, onlyActive,
      });
      const { data } = await api.get(`/products?${qs}`);
      setProducts(data.items || []);
      setTotal(data.total || 0);
    } catch (_err) {
      setProducts([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(loadProducts, 250);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  const handleSelect = (p) => {
    if (!p) return;
    onSelect(p);
    setSearch("");
    setNotice("");
    // Devolver foco al input para búsqueda rápida en secuencia
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const onKeyDown = async (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const q = search.trim();
    if (!q) return;

    // 1) Match EXACTO de SKU en resultados actuales
    const qLower = q.toLowerCase();
    const exact = products.find(
      (p) => (p.sku || "").toLowerCase() === qLower,
    );
    if (exact) return handleSelect(exact);

    // 2) Una sola coincidencia total en DB
    if (total === 1 && products.length === 1) {
      return handleSelect(products[0]);
    }

    // 3) Buscar SKU exacto en toda la DB
    try {
      const { data: prod } = await api.get(
        `/products/by-sku/${encodeURIComponent(q)}${onlyActive ? "?only_active=true" : ""}`,
      );
      if (prod) return handleSelect(prod);
    } catch (_err) { /* 404 → fallthrough */ }

    // 4) Sin match único
    setNotice(total === 0
      ? "Sin coincidencias"
      : "Sin coincidencia única — elige uno de la lista");
  };

  const rows = useMemo(() => products.slice(0, pageSize), [products, pageSize]);

  return (
    <div className="flex flex-col gap-2" data-testid={`${testidPrefix}-container`}>
      <div className="relative">
        <Search className="w-4 h-4 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
        <Input
          ref={inputRef}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setNotice(""); }}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          className="pl-8"
          data-testid={`${testidPrefix}-input`}
        />
      </div>
      {notice && (
        <div className="text-xs text-amber-600 dark:text-amber-400" data-testid={`${testidPrefix}-notice`}>
          {notice}
        </div>
      )}
      <div
        className="border border-border rounded-md max-h-[260px] overflow-y-auto"
        data-testid={`${testidPrefix}-results`}
      >
        {loading && rows.length === 0 ? (
          <div className="p-4 text-xs text-center text-muted-foreground">Buscando…</div>
        ) : rows.length === 0 ? (
          <div className="p-4 text-xs text-center text-muted-foreground">
            {search ? "Sin coincidencias" : "Escribe para buscar…"}
          </div>
        ) : rows.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => handleSelect(p)}
            className="w-full text-left p-2 hover:bg-accent border-b border-border/60 text-sm flex justify-between gap-2"
            data-testid={`${testidPrefix}-option-${p.sku}`}
          >
            <div className="truncate">
              <span className="data-cell text-xs text-muted-foreground mr-2">{p.sku}</span>
              {p.name}
            </div>
            <div className="data-cell font-semibold text-primary text-xs shrink-0">
              {showCost
                ? <>Costo: {formatDOP(p.cost)}</>
                : formatDOP(p.price)}
            </div>
          </button>
        ))}
      </div>
      {total > rows.length && (
        <div className="text-[10px] text-muted-foreground text-right" data-testid={`${testidPrefix}-hint`}>
          Mostrando {rows.length} de {total}. Refina la búsqueda para ver más.
        </div>
      )}
    </div>
  );
}
